import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Stack from 'react-bootstrap/Stack';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import {
  CUTOFF_DNR,
  CUTOFF_HR,
  CUTOFF_RWC,
  BLUE_COLORS,
  getRecommendationCutoff,
  GREEN_YELLOW_RED_COLORS,
} from '../../client-dashboard/candidate-scoring-util';
import { orve, asve } from '../shared/orve';
import { getCandidateValuesText, getScoreColors } from '../shared/report-util';
import { useAuth } from '../../../providers/auth-provider';
import { getPercentileAttributesConstants } from '../../../api/dashboards-api';
import { REPORT_TYPE } from '../../../utils/constants';

function CandidateReportSummary({
  candidate,
  withAssessmentText = false,
  withRecommendationSummary = false,
  displayPercentile = true,
  reportType = REPORT_TYPE.coaching,
  remoteStaffing = false,
}) {
  const { isDevelop, isSelect } = useAuth();
  const [assessmentText, setAssessmentText] = useState({});
  const [overall, setOverall] = useState(0);
  const [percentileAttributes, setPercentileAttributes] = useState(getPercentileAttributesConstants().default);

  const getAssessmentContent = (title, value) => (
    <div>
      <div className="text-bolder">{title}</div>
      <div style={{ fontSize: '.85em' }}>{value}</div>
    </div>
  );

  const getOverallLegend = useCallback(() => {
    switch (overall) {
      case 100:
        return (
          <div>
            <div>Highly</div>
            <div>Recommend</div>
          </div>
        );
      case 75:
        return (
          <div>
            <div>Recommend</div>
          </div>
        );
      case 50:
        return (
          <div>
            <div>Recommend</div>
            <div>with Caution</div>
          </div>
        );
      default:
        return (
          <div>
            <div>Do Not</div>
            <div>Recommend</div>
          </div>
        );
    }
  }, [overall]);

  const getOverallColors = useCallback(() => {
    const colorPalette = reportType === REPORT_TYPE.development ? BLUE_COLORS : GREEN_YELLOW_RED_COLORS;
    switch (overall) {
      case 100:
        return (
          <>
            <Cell fill={colorPalette.highlyRecommend.primary} />
            <Cell fill={colorPalette.highlyRecommend.secondary} />
          </>
        );
      case 75:
        return (
          <>
            <Cell fill={colorPalette.recommend.primary} />
            <Cell fill={colorPalette.recommend.secondary} />
          </>
        );
      case 50:
        return (
          <>
            <Cell fill={colorPalette.recommendWithCaution.primary} />
            <Cell fill={colorPalette.recommendWithCaution.secondary} />
          </>
        );
      default:
        return (
          <>
            <Cell fill={colorPalette.doNotRecommend.primary} />
            <Cell fill={colorPalette.doNotRecommend.secondary} />
          </>
        );
    }
  }, [overall]);

  useEffect(() => {
    if (candidate) {
      setAssessmentText(asve(candidate));
      let cutoff;
      if (reportType === REPORT_TYPE.staffing) {
        if (remoteStaffing) {
          setPercentileAttributes(getPercentileAttributesConstants().remote);
          cutoff = getRecommendationCutoff(candidate, 'remote');
        } else {
          setPercentileAttributes(getPercentileAttributesConstants().onsite);
          cutoff = getRecommendationCutoff(candidate, 'onsite');
        }
      } else {
        cutoff = getRecommendationCutoff(candidate);
      }
      switch (cutoff) {
        case CUTOFF_HR:
          setOverall(100);
          break;
        case CUTOFF_RWC:
          setOverall(50);
          break;
        case CUTOFF_DNR:
          setOverall(25);
          break;
        default:
          setOverall(75);
      }
    }
  }, [candidate]);

  return (
    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
      {candidate && (
        <>
          <Row className="justify-content-center pt-4">Predictive Summary</Row>
          <Row className="justify-content-center w-25 mx-auto pt-3">
            <hr style={{ borderTop: '2px solid' }} />
          </Row>
          <Row className="justify-content-center pt-3 pb-3">
            <Stack direction="horizontal" className="justify-content-evenly d-none d-md-flex">
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">{isDevelop() ? 'Potential' : 'Recommendation'}</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[{ value: overall }, { value: 100 - overall }]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getOverallColors()}
                      </Pie>
                      {displayPercentile && (
                        <Legend
                          content={getOverallLegend()}
                          verticalAlign="middle"
                          className="candidate-profile-recommend-legend"
                        />
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">Performance</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[
                          { value: candidate.percentiles[percentileAttributes.performance] },
                          { value: 100 - candidate.percentiles[percentileAttributes.performance] },
                        ]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getScoreColors(candidate.percentiles[percentileAttributes.performance], reportType)}
                      </Pie>
                      {displayPercentile && (
                        <Legend
                          content={<div>{`${candidate.percentiles[percentileAttributes.performance]}%`}</div>}
                          verticalAlign="middle"
                          className="candidate-profile-summary-legend"
                        />
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">Loyalty</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[
                          { value: candidate.percentiles[percentileAttributes.loyalty] },
                          { value: 100 - candidate.percentiles[percentileAttributes.loyalty] },
                        ]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getScoreColors(candidate.percentiles[percentileAttributes.loyalty], reportType)}
                      </Pie>
                      {displayPercentile && (
                        <Legend
                          content={<div>{`${candidate.percentiles[percentileAttributes.loyalty]}%`}</div>}
                          verticalAlign="middle"
                          className="candidate-profile-summary-legend"
                        />
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
            </Stack>
            <Stack direction="vertical" className="justify-content-evenly d-md-none" gap={5}>
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">Recommendation</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[{ value: overall }, { value: 100 - overall }]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getOverallColors()}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">Performance</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[
                          { value: candidate.percentiles[percentileAttributes.performance] },
                          { value: 100 - candidate.percentiles[percentileAttributes.performance] },
                        ]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getScoreColors(candidate.percentiles[percentileAttributes.performance], reportType)}
                      </Pie>
                      <Legend
                        content={<div>{`${candidate.percentiles[percentileAttributes.performance]}%`}</div>}
                        verticalAlign="middle"
                        className="candidate-profile-summary-legend"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
              <Stack className="align-items-center">
                <div className="text-bolder text-larger">Loyalty</div>
                <div style={{ height: '5em', width: '10em' }}>
                  <ResponsiveContainer width="100%" aspect={1}>
                    <PieChart>
                      <Pie
                        data={[
                          { value: candidate.percentiles[percentileAttributes.loyalty] },
                          { value: 100 - candidate.percentiles[percentileAttributes.loyalty] },
                        ]}
                        dataKey="value"
                        innerRadius={50}
                        outerRadius={70}
                        startAngle={180}
                        endAngle={0}
                      >
                        {getScoreColors(candidate.percentiles[percentileAttributes.loyalty], reportType)}
                      </Pie>
                      <Legend
                        content={<div>{`${candidate.percentiles[percentileAttributes.loyalty]}%`}</div>}
                        verticalAlign="middle"
                        className="candidate-profile-summary-legend"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Stack>
            </Stack>
          </Row>
          {withRecommendationSummary && (
            <>
              <Row className="justify-content-center pt-4">Recommendation Summary</Row>
              <Row className="justify-content-center w-25 mx-auto pt-3">
                <hr style={{ borderTop: '2px solid' }} />
              </Row>
              <Row>
                <div className="text-bolder mx-auto pt-2 pb-2 candidate-profile-overall-content">
                  {orve(
                    candidate.percentiles[percentileAttributes.performance],
                    candidate.percentiles[percentileAttributes.loyalty],
                    true
                  )}
                </div>
              </Row>
            </>
          )}
          {withAssessmentText && reportType !== REPORT_TYPE.staffing && (
            <>
              <Row className="pt-3 pb-2 mx-auto candidate-profile-overall-content">
                <Col>{getAssessmentContent('Personality', assessmentText.personality)}</Col>
                <Col>{getAssessmentContent('Acumen', assessmentText.acumen)}</Col>
                <Col>
                  {getAssessmentContent(`${getCandidateValuesText(isSelect(), true)} Fit`, assessmentText.values)}
                </Col>
              </Row>
              <Row className="pt-3 pb-2 mx-auto candidate-profile-overall-content">
                <Col>{getAssessmentContent('Situational Judgment', assessmentText.judgment)}</Col>
                <Col>{getAssessmentContent('Experience', assessmentText.experience)}</Col>
                <Col>{getAssessmentContent('', '')}</Col>
              </Row>
            </>
          )}
        </>
      )}
    </Container>
  );
}

export default CandidateReportSummary;
